import { useState, useEffect } from 'react'

function LandingPage({ onGetStarted, onLogin }) {
  const [isVisible, setIsVisible] = useState(false)
  const [headerSolid, setHeaderSolid] = useState(false)

  useEffect(() => {
    setIsVisible(true)
    const handleScroll = () => setHeaderSolid(window.scrollY > 60)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const features = [
    { icon: '\u26A1', title: 'Instant Results', desc: 'Shear, moment, and reaction analysis in seconds. No software installs, no waiting.' },
    { icon: '\uD83D\uDCCB', title: 'Calculation Packages', desc: 'Generate professional, stamp-ready PDF packages with step-by-step code references.' },
    { icon: '\uD83D\uDD12', title: 'Code Compliant', desc: 'ACI 318-19, AISC 360, ASCE 7. Every formula traced to the governing code section.' },
    { icon: '\u2601\uFE0F', title: 'Work From Anywhere', desc: 'Cloud-based platform. Office, job site, or home. Your work follows you across devices.' },
  ]

  const suiteCategories = [
    { category: 'Analysis Tools', items: [{ name: 'Beam Analysis', coming: false }, { name: 'Continuous Beam Analysis', coming: true }, { name: 'Frame Analysis', coming: true }] },
    { category: 'Concrete Design', items: [{ name: 'Beam Design', coming: false }, { name: 'Development Length', coming: false }, { name: 'Column Interaction', coming: true }, { name: 'Spread Footing Design', coming: true }] },
    { category: 'Steel Design', items: [{ name: 'Beam Design', coming: true }, { name: 'Column Design', coming: true }, { name: 'Connection Design', coming: true }] },
  ]

  const LogoSVG = ({ size = 32 }) => (
    <svg viewBox="0 0 32 32" width={size} height={size}>
      <defs><linearGradient id={'lg' + size} x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1e3a5f"/><stop offset="100%" stopColor="#0f2440"/></linearGradient></defs>
      <rect x="0" y="0" width="32" height="32" rx="6" fill={'url(#lg' + size + ')'}/>
      <polygon points="8,24 8,8 11,8 16,15 21,8 24,8 24,24 21,24 21,13 16,20 11,13 11,24" fill="white"/>
    </svg>
  )

  return (
    <div style={st.page}>
      <style>{`
        @keyframes grid-scroll{0%{transform:translateY(0)}100%{transform:translateY(44px)}}
        @keyframes glow-pulse{0%,100%{opacity:.4}50%{opacity:.7}}
        *{margin:0;padding:0;box-sizing:border-box}
        html{scroll-behavior:smooth}
        body{overflow-x:hidden}
      `}</style>

      <header style={{ ...st.header, background: headerSolid ? 'rgba(8,12,24,0.95)' : 'transparent', borderBottom: headerSolid ? '1px solid rgba(30,58,95,0.2)' : '1px solid transparent', backdropFilter: headerSolid ? 'blur(20px)' : 'none', WebkitBackdropFilter: headerSolid ? 'blur(20px)' : 'none' }}>
        <div style={st.headerInner}>
          <div style={st.logoGroup}><LogoSVG /><span style={st.logoText}>MRVN</span></div>
          <div style={st.headerBtns}>
            <button onClick={onLogin} style={st.loginBtn}>Log In</button>
            <button onClick={onGetStarted} style={st.ctaBtn}>Get Started</button>
          </div>
        </div>
      </header>

      <section style={st.hero}>
        <div style={st.gridBg}></div>
        <div style={st.glow1}></div>
        <div style={st.glow2}></div>
        <div style={{ ...st.heroContent, opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)' }}>
          <h1 style={st.heroBrand}>MRVN</h1>
          <p style={st.heroTagline}>Structural Analysis &amp; Design Platform</p>
          <p style={st.heroSub}>Professional-grade engineering tools built for practicing engineers. Code-compliant. Accessible from any device.</p>
          <div style={st.heroBtns}>
            <button onClick={onGetStarted} style={st.heroP}>Get Started <span>&rarr;</span></button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} style={st.heroS}>Learn More</button>
          </div>
        </div>
      </section>

      <section id="features" style={st.section}>
        <div style={st.inner}>
          <h2 style={st.secTitle}>Built for Engineers</h2>
          <p style={st.secSub}>Every feature designed around how structural engineers actually work.</p>
          <div style={st.fGrid}>
            {features.map((f, i) => (
              <div key={i} style={st.fCard}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(30,58,95,0.5)'; e.currentTarget.style.background = 'linear-gradient(135deg,rgba(30,58,95,0.1),rgba(15,36,64,0.05))' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'rgba(255,255,255,0.015)' }}>
                <div style={st.fIcon}>{f.icon}</div>
                <h3 style={st.fTitle}>{f.title}</h3>
                <p style={st.fDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={st.section}>
        <div style={st.inner}>
          <h2 style={st.secTitle}>Engineering Design Suite</h2>
          <p style={st.secSub}>A growing library of professional structural analysis and design tools.</p>
          <div style={st.suiteGrid}>
            {suiteCategories.map((cat, ci) => (
              <div key={ci} style={st.suiteCat}>
                <h3 style={st.suiteCatT}>{cat.category}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                  {cat.items.map((item, ii) => (
                    <div key={ii} style={st.suiteItem}>
                      <span style={{ fontSize: '0.93rem', fontWeight: 500, color: item.coming ? '#cbd5e1' : '#f1f5f9' }}>{item.name}</span>
                      {item.coming && <span style={st.coming}>Coming Soon</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={st.ctaSec}>
        <div style={st.ctaIn}>
          <h2 style={st.ctaT}>Start using MRVN today</h2>
          <p style={st.ctaS}>Create an account and run your first analysis in minutes.</p>
          <button onClick={onGetStarted} style={st.ctaBig}>Create Your Account <span>&rarr;</span></button>
        </div>
      </section>

      <footer style={st.footer}>
        <div style={st.footerIn}>
          <div>
            <div style={st.logoGroup}><LogoSVG /><span style={st.logoText}>MRVN</span></div>
            <p style={{ fontSize: '0.83rem', color: '#475569', marginTop: '0.4rem' }}>Structural engineering analysis and design platform.</p>
          </div>
          <p style={{ fontSize: '0.83rem', color: '#475569' }}>&copy; 2026 MRVN. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

const st = {
  page: { background: '#080c18', color: '#e2e8f0', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', minHeight: '100vh', overflowX: 'hidden' },
  header: { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 2rem', transition: 'all 0.3s ease' },
  headerInner: { maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 0' },
  logoGroup: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  logoText: { fontWeight: 800, fontSize: '1.3rem', letterSpacing: '0.08em', color: '#f1f5f9' },
  headerBtns: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  loginBtn: { padding: '0.5rem 1.25rem', background: 'transparent', border: '1px solid rgba(148,163,184,0.25)', borderRadius: '6px', color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' },
  ctaBtn: { padding: '0.5rem 1.25rem', background: '#3b82f6', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' },
  hero: { position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '8rem 2rem 3rem', overflow: 'hidden' },
  gridBg: { position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(59,130,246,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,0.03) 1px,transparent 1px)', backgroundSize: '44px 44px', animation: 'grid-scroll 10s linear infinite' },
  glow1: { position: 'absolute', width: '700px', height: '700px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(30,58,95,0.12) 0%,transparent 70%)', top: '10%', left: '50%', transform: 'translateX(-50%)', animation: 'glow-pulse 7s ease-in-out infinite' },
  glow2: { position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(15,36,64,0.1) 0%,transparent 70%)', bottom: '15%', right: '10%', animation: 'glow-pulse 9s ease-in-out infinite 3s' },
  heroContent: { textAlign: 'center', maxWidth: '800px', position: 'relative', zIndex: 2 },
  heroBrand: { fontSize: 'clamp(4rem,10vw,7rem)', fontWeight: 900, letterSpacing: '0.12em', lineHeight: 1, marginBottom: '1rem', background: 'linear-gradient(180deg,#f1f5f9 0%,#94a3b8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
  heroTagline: { fontSize: 'clamp(1.1rem,2.5vw,1.35rem)', fontWeight: 500, color: '#3b82f6', letterSpacing: '0.02em', marginBottom: '1rem' },
  heroSub: { fontSize: '1.05rem', lineHeight: 1.65, color: '#64748b', maxWidth: '500px', margin: '0 auto 2rem' },
  heroBtns: { display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' },
  heroP: { padding: '0.8rem 2rem', background: '#3b82f6', border: 'none', borderRadius: '8px', color: 'white', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 0 30px rgba(59,130,246,0.2)', fontFamily: 'inherit' },
  heroS: { padding: '0.8rem 2rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#cbd5e1', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit' },
  section: { padding: '4rem 2rem', borderTop: '1px solid rgba(255,255,255,0.04)' },
  inner: { maxWidth: '1100px', margin: '0 auto' },
  secTitle: { fontSize: '1.8rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#f1f5f9', textAlign: 'center', marginBottom: '0.6rem' },
  secSub: { fontSize: '1rem', color: '#64748b', textAlign: 'center', marginBottom: '2.5rem', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' },
  fGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '1rem' },
  fCard: { border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '1.75rem', transition: 'all 0.35s ease', cursor: 'default', background: 'rgba(255,255,255,0.015)' },
  fIcon: { fontSize: '1.5rem', marginBottom: '0.75rem' },
  fTitle: { fontSize: '1.02rem', fontWeight: 700, color: '#e2e8f0', marginBottom: '0.45rem' },
  fDesc: { fontSize: '0.88rem', lineHeight: 1.6, color: '#64748b' },
  suiteGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1.25rem' },
  suiteCat: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '1.5rem' },
  suiteCatT: { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#3b82f6', marginBottom: '1rem', paddingBottom: '0.7rem', borderBottom: '1px solid rgba(30,58,95,0.25)' },
  suiteItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0' },
  coming: { fontSize: '0.72rem', fontWeight: 600, color: '#475569', background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.1)', padding: '0.2rem 0.55rem', borderRadius: '100px' },
  ctaSec: { padding: '4rem 2rem', borderTop: '1px solid rgba(255,255,255,0.04)' },
  ctaIn: { maxWidth: '580px', margin: '0 auto', textAlign: 'center' },
  ctaT: { fontSize: '1.8rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '0.6rem', letterSpacing: '-0.02em' },
  ctaS: { fontSize: '1rem', color: '#64748b', marginBottom: '2rem' },
  ctaBig: { padding: '0.85rem 2rem', background: '#3b82f6', border: 'none', borderRadius: '8px', color: 'white', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 0 40px rgba(59,130,246,0.18)', fontFamily: 'inherit' },
  footer: { padding: '2.5rem 2rem', borderTop: '1px solid rgba(255,255,255,0.04)' },
  footerIn: { maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' },
}

export default LandingPage
